import axios from 'axios'
import { defineStore } from 'pinia'
import { generateError } from '@/utils/errors'
import { useAuth } from '@/stores/auth'

/** Generate auth info from a user. Needs:
 *  - id
 *  - access || access_token
 *  - refresh || refresh_token
 *  @param user {Object} The user data to generate auth from
 */
function generateAuth(user) {
  if (!user) {
    return null
  }

  return {
    id: user.user_id || '',
    organization: user.organization_id || '',
    token: user.access_token || user.access || '',
    refresh: user.refresh_token || user.refresh || '',
  }
}

export const useAccount = defineStore('account', {
  state: () => ({
    isLoading: false,
    id: null,
    username: null,
    email: null,
    organization: null,
    avatar: null,
    first_name: null,
    last_name: null,
    has_usable_password: null,
    permissions: [],
    groups: null,
    attrs: null,
    featureFlags: [],
  }),
  actions: {
    async get(id) {
      let getId = id || this.id
      if (!getId) {
        return null
      }

      this.isLoading = true

      try {
        const user = await axios.get(`/core/v1/users/${getId}`)

        this.setData(user)

        return user || null
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async create(data) {
      this.isLoading = true

      try {
        const authResponse = await axios.post('/core/v1/users', data)
        const auth = useAuth()

        auth.storeAuth(generateAuth(authResponse))

        this.setData({
          ...authResponse,
          id: authResponse.id,
        })

        this.isLoading = false

        return authResponse || null
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async update(id, data) {
      const accountId = id || this.id
      if (!accountId) {
        throw generateError({
          message: 'Unable to update account: No ID provided',
        })
      }

      try {
        const user = await axios.patch(`/core/v1/users/${accountId}`, data)
        return user
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async updatePassword(data) {
      this.isLoading = true

      try {
        const user = await axios.patch(`/core/v1/user/reset-password`, data)
        return user
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async login(data) {
      this.isLoading = true

      try {
        const authResponse = await axios.post('/core/v1/users/token', data)
        const auth = useAuth()

        auth.storeAuth(generateAuth(authResponse))

        const userResponse = await this.get(authResponse.user_id)

        // Set organization from auth response AFTER get() to avoid being overwritten by setData
        this.organization = authResponse.organization_id || null

        return {
          ...authResponse,
          ...userResponse,
        }
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async googleAuth(data) {
      this.isLoading = true

      try {
        const authResponse = await axios.post('/core/v1/users/google', data)
        const auth = useAuth()

        auth.storeAuth(generateAuth(authResponse))

        const userResponse = await this.get(authResponse.user_id)

        // Set organization from auth response AFTER get() to avoid being overwritten by setData
        this.organization = authResponse.organization_id || null

        return {
          ...authResponse,
          ...userResponse,
        }
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async refresh(token) {
      this.isLoading = true

      try {
        const auth = useAuth()
        const authResponse = await axios.post(
          '/core/v1/users/token/refresh',
          {
            refresh_token: token,
          },
          {
            headers: { Authorization: undefined },
          },
        )

        auth.storeAuth(generateAuth(authResponse))

        const userResponse = await this.get(authResponse.user_id)

        // Set organization from auth response AFTER get() to avoid being overwritten by setData
        this.organization = authResponse.organization_id || null

        return {
          ...authResponse,
          ...userResponse,
        }
      } catch (err) {
        throw generateError(err)
      } finally {
        this.isLoading = false
      }
    },
    async surveySubmit(userId, data) {
      this.isLoading = true

      try {
        const finishSurvey = await axios.post(`/core/v1/users/${userId}/surveys`, data)

        return finishSurvey
      } catch (err) {
        throw generateError(err)
      }
    },
    logout() {
      const auth = useAuth()

      this.setData({
        id: '',
        username: '',
        email: '',
      })

      return auth.logout()
    },
    setData(data) {
      this.id = data.id
      this.username = data.username
      this.email = data.email
      this.organization = data.organization || null
      this.avatar = data.avatar || null
      this.first_name = data.first_name || null
      this.last_name = data.last_name || null
      this.has_usable_password = data.has_usable_password || null
      this.permissions = data.permissions || []
      this.groups = data.groups || null
      this.attrs = data.attrs || null
      this.featureFlags = data.feature_flags || []
    },
  },
})
